import React from 'react';
import ClubScreenView, { DEFAULT_SCREEN_SETTINGS, SLIDE_IDS, SLIDE_LABELS, activePosters, buildRotation, comingUpItems, matchesToday, noticeboardItems, openForEntry, screenPhotos } from './ClubScreenView';
import { API } from '../api';
import { Section, Segmented, Toggle } from './ui';

/*
 * Driving the club screen from a laptop.
 *
 * The preview is the real ClubScreenView, not a drawing of it — same
 * component, sized in container-query units so it renders identically at
 * 420px and at 1920px. Anything that would be wrong on the TV is wrong here
 * too, which is the point: an admin should be able to check the screen
 * without walking into the other room.
 *
 * Settings live in localStorage rather than on the club record, because they
 * describe a screen and not a club — the TV in the clubrooms and the laptop
 * in the office can reasonably want different rotations.
 */

const STORAGE_KEY = 'greensheet_club_screen_settings';

export function loadScreenSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SCREEN_SETTINGS;
    const saved = JSON.parse(raw);
    return {
      ...DEFAULT_SCREEN_SETTINGS,
      ...saved,
      enabled: { ...DEFAULT_SCREEN_SETTINGS.enabled, ...(saved.enabled || {}) },
    };
  } catch {
    // A private window, cleared site data, or a half-written value: the
    // defaults are a perfectly good screen.
    return DEFAULT_SCREEN_SETTINGS;
  }
}

function saveScreenSettings(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* not fatal */ }
}

function ClubScreenControl({ clubs, comps, dashLeaderboard, onOpenScreen, recentMatches, selectedClubId, upcomingMatches }) {
  const [settings, setSettings] = React.useState(loadScreenSettings);
  const [posters, setPosters] = React.useState([]);
  const [photos, setPhotos] = React.useState([]);
  const [events, setEvents] = React.useState([]);

  /* Only to say how many are switched on. The preview renders the real
     ClubScreenView, which fetches its own. */
  React.useEffect(() => {
    if (!selectedClubId) { setPosters([]); return undefined; }
    let cancelled = false;
    fetch(`${API}/api/posters?clubId=${selectedClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setPosters(Array.isArray(d) ? d : []); })
      .catch(() => {});
    fetch(`${API}/api/gallery/screen?clubId=${selectedClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setPhotos(Array.isArray(d) ? d : []); })
      .catch(() => {});
    fetch(`${API}/api/events?clubId=${selectedClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setEvents(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedClubId]);

  const update = patch => setSettings(s => {
    const next = { ...s, ...patch };
    saveScreenSettings(next);
    return next;
  });
  const toggleSlide = id => update({ enabled: { ...settings.enabled, [id]: !settings.enabled[id] } });

  const data = { clubs, comps, dashLeaderboard, recentMatches, selectedClubId, upcomingMatches };
  const rotation = buildRotation({ comps, dashLeaderboard, events, photos, posters, recentMatches, settings, upcomingMatches });
  const today = matchesToday(upcomingMatches, recentMatches);
  const open = openForEntry(comps);
  const standings = ((dashLeaderboard && dashLeaderboard.standings) || []).filter(s => s.type === 'player');
  const live = activePosters(posters);
  const livePhotos = screenPhotos(photos);
  /* Posters hold a place each now, so the rotation array is longer than the
     number of kinds of slide. An admin counting "slides" means the latter. */
  const distinctSlides = new Set(rotation).size;

  /* What each slide would put on the screen right now. Shown next to its
     switch so an admin can see why something is not in the rotation without
     having to turn it on and wait. */
  const contents = {
    draw: today.length ? `${today.length} match${today.length === 1 ? '' : 'es'} today` : 'Nothing on today',
    scores: today.length ? `${today.filter(m => m.completed).length} of ${today.length} finished` : 'Nothing on today',
    ladder: standings.length ? `Top ${Math.min(8, standings.length)} of ${standings.length}` : 'No results yet',
    comingUp: (() => {
      const rows = comingUpItems(open, events);
      if (!rows.length) return 'Nothing coming up';
      const evs = rows.filter(r => r.isEvent).length;
      const comps2 = rows.length - evs;
      return [comps2 ? `${comps2} open for entry` : null, evs ? `${evs} event${evs === 1 ? '' : 's'}` : null]
        .filter(Boolean).join(' · ');
    })(),
    noticeboard: (() => {
      const notices = noticeboardItems(events, posters);
      if (!notices.length) return 'Nothing pinned up';
      const withPoster = events.filter(e => !e.cancelled).length - notices.length;
      return notices.length + ' notice' + (notices.length === 1 ? '' : 's')
        + (withPoster > 0 ? ' · ' + withPoster + ' shown as posters instead' : '');
    })(),
    posters: live.length
      ? `${live.length} showing${posters.length > live.length ? ` of ${posters.length}` : ''}`
      : (posters.length ? `${posters.length} made, none switched on` : 'None yet'),
  };
  const isEmpty = {
    draw: !today.length, scores: !today.length, ladder: !standings.length,
    comingUp: !comingUpItems(open, events).length,
    noticeboard: !noticeboardItems(events, posters).length,
    posters: !live.length,
    photos: !livePhotos.length,
  };

  return (
    <div className="page-container">
      <div className="club-control">

        <div className="club-control-main">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gs-space-3)', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>Club screen</h2>
            <span style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
              {rotation.length
                ? `${distinctSlides} slide${distinctSlides === 1 ? '' : 's'} in the rotation · ${settings.seconds}s each`
                : 'Nothing to show yet'}
            </span>
            <button
              className="w3-button w3-round"
              style={{ marginLeft: 'auto', minHeight: 'var(--gs-control-md)', padding: '0 22px', background: 'var(--gs-action-fill, var(--club-10))', color: '#fff', fontWeight: 600 }}
              onClick={onOpenScreen}>
              Open on this screen
            </button>
          </div>

          <p style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)', marginTop: 'var(--gs-space-3)' }}>
            Open it on the machine wired to the TV and leave it. There is nothing to
            click — it updates itself as scores come in.
          </p>

          <div style={{ marginTop: 'var(--gs-space-4)' }}>
            <ClubScreenView {...data} settings={settings} preview />
          </div>
        </div>

        <div className="club-control-side">
          <Section label="Slides in the rotation">
            {SLIDE_IDS.map(id => (
              <div key={id} className="club-control-row">
                <Toggle
                  ariaLabel={SLIDE_LABELS[id]}
                  checked={!!settings.enabled[id]}
                  onChange={() => toggleSlide(id)} />
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--gs-text-base)', color: 'var(--gs-text)' }}>{SLIDE_LABELS[id]}</div>
                  <div style={{
                    fontSize: 'var(--gs-text-sm)',
                    marginTop: 2,
                    color: isEmpty[id] ? 'var(--gs-text-faint)' : 'var(--gs-text-muted)',
                  }}>
                    {contents[id]}
                    {isEmpty[id] && settings.enabled[id] && !settings.showEmpty ? ' · skipped' : ''}
                  </div>
                </div>
              </div>
            ))}
          </Section>

          <Section label="When a slide has nothing to show">
            <Segmented
              ariaLabel="When a slide has nothing to show"
              options={[{ value: 'skip', label: 'Skip it' }, { value: 'empty', label: 'Show it empty' }]}
              value={settings.showEmpty ? 'empty' : 'skip'}
              onChange={v => update({ showEmpty: v === 'empty' })} />
            <p style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-faint)', marginTop: 'var(--gs-space-3)', marginBottom: 0 }}>
              Skipping keeps the screen worth looking at on a quiet morning. It also
              makes the rotation shorter, so each slide comes round sooner.
            </p>
          </Section>

          <Section label="Seconds per slide">
            <Segmented
              ariaLabel="Seconds per slide"
              options={[10, 15, 25, 40]}
              value={settings.seconds}
              onChange={v => update({ seconds: parseInt(v, 10) })} />
          </Section>
        </div>

      </div>
    </div>
  );
}

export default ClubScreenControl;
